'use client';
import React, { useEffect, useRef, useState } from 'react';

const CODE_SNIPPET = `// Batch dedup: find & merge duplicate records
const table = base.getTable('Contacts');
const records = await table.selectRecordsAsync({
  fields: ['Email', 'Name', 'Status']
});

// Group by email
const byEmail = new Map();
for (const rec of records.records) {
  const email = rec.getCellValueAsString('Email');
  if (!byEmail.has(email)) {
    byEmail.set(email, []);
  }
  byEmail.get(email).push(rec);
}

// Identify duplicates
const toDelete = [];
for (const [email, dupes] of byEmail) {
  if (dupes.length > 1) {
    // Keep first, delete rest
    toDelete.push(...dupes.slice(1).map(r => r.id));
  }
}

// Batch delete (50 at a time)
while (toDelete.length > 0) {
  await table.deleteRecordsAsync(toDelete.splice(0, 50));
}
output.text(\`Removed \${toDelete.length} duplicates.\`);`;

const TOKENS = [
  { re: /(\/\/[^\n]*)/, cls: 'text-green-400 opacity-60' },
  { re: /\b(const|let|var|for|of|if|while|new|await|async|return)\b/, cls: 'text-violet-400' },
  { re: /\b(Map|Array|true|false|null|undefined)\b/, cls: 'text-blue-400' },
  { re: /('[^']*'|`[^`]*`)/, cls: 'text-yellow-300 opacity-80' },
  { re: /\b(\d+)\b/, cls: 'text-orange-400' },
  { re: /\b(table|records|byEmail|email|dupes|toDelete|rec|output|base)\b/, cls: 'text-fg' },
];

function ColorizedCode({ code }: { code: string }) {
  const [mounted, setMounted] = React.useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const lines = code.split('\n');
  return (
    <div className="font-mono text-[11px] leading-relaxed overflow-auto max-h-72 pr-2">
      {lines.map((line, i) => (
        <div key={i} className="flex gap-3 hover:bg-white/[0.02] px-1 rounded">
          <span className="select-none min-w-[1.5rem] text-right" style={{ color: '#3F3F46' }}>{i + 1}</span>
          {mounted ? (
            <span style={{ color: '#A1A1AA' }} dangerouslySetInnerHTML={{ __html: highlightLine(line) }} />
          ) : (
            <span style={{ color: '#A1A1AA' }}>{line}</span>
          )}
        </div>
      ))}
    </div>
  );
}

function highlightLine(line: string): string {
  // Simple keyword highlight
  return line
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/(\/\/[^\n]*)/g, '<span style="color:#4ade80;opacity:0.7">$1</span>')
    .replace(/\b(const|let|var|for|of|if|while|new|await|async|return|function)\b/g, '<span style="color:#a78bfa">$1</span>')
    .replace(/('(?:[^'\\]|\\.)*'|`(?:[^`\\]|\\.)*`)/g, '<span style="color:#fde047;opacity:0.8">$1</span>')
    .replace(/\b(\d+)\b/g, '<span style="color:#fb923c">$1</span>');
}

export default function FeaturedArticle() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.fade-in-up').forEach((el, i) => {
              setTimeout(() => el.classList.add('visible'), i * 120);
            });
          }
        });
      },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-24 px-6 max-w-7xl mx-auto">
      <div className="mb-12 fade-in-up">
        <div className="flex items-center gap-3 mb-4">
          <span className="font-mono text-xs tracking-widest uppercase" style={{ color: '#52525B' }}>// featured_deep_dive</span>
          <div className="flex-1 h-px" style={{ background: 'rgba(124,58,237,0.15)' }} />
        </div>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Main featured card */}
        <div className="lg:col-span-3 rounded-2xl overflow-hidden fade-in-up"
          style={{ background: '#0F0E1A', border: '1px solid rgba(124,58,237,0.25)' }}>
          {/* Card header */}
          <div className="p-6 pb-0">
            <div className="flex items-center gap-2 mb-4">
              <span className="tag-pill">Scripting</span>
              <span className="tag-pill difficulty-advanced">Advanced</span>
              <span className="ml-auto font-mono text-xs" style={{ color: '#52525B' }}>9 min read</span>
            </div>
            <h3 className="text-xl font-bold leading-snug mb-3" style={{ color: '#F4F4F5' }}>
              selectRecordsAsync vs selectRecords: When 50ms becomes 5 seconds
            </h3>
            <p className="text-sm leading-relaxed mb-4" style={{ color: '#71717A' }}>
              The async method is non-blocking but burns automation runs. After profiling 40+ production bases,
              here&apos;s the decision matrix that saves you from 3 a.m. debugging sessions.
            </p>
            <div className="flex items-center gap-4 text-xs font-mono mb-4" style={{ color: '#52525B' }}>
              <span>⬡ 247 forks</span>
              <span>· Updated Feb 24, 2026</span>
              <span>· by Marcus Webb</span>
            </div>
          </div>

          {/* Code block */}
          <div className="mx-6 mb-6">
            <div className="code-block">
              <div className="flex items-center justify-between px-4 py-2" style={{ borderBottom: '1px solid rgba(124,58,237,0.15)', background: 'rgba(30,27,75,0.5)' }}>
                <div className="flex gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#ef4444' }} />
                  <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#f59e0b' }} />
                  <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#22c55e' }} />
                </div>
                <span className="font-mono text-[10px]" style={{ color: '#52525B' }}>dedup-contacts.js</span>
                <button className="font-mono text-[10px] transition-colors" style={{ color: '#52525B' }}
                  onMouseEnter={e => (e.currentTarget as HTMLElement).style.color = '#C4B5FD'}
                  onMouseLeave={e => (e.currentTarget as HTMLElement).style.color = '#52525B'}
                >
                  copy
                </button>
              </div>
              <div className="p-4">
                <ColorizedCode code={CODE_SNIPPET} />
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="px-6 pb-6">
            <button className="w-full py-3 rounded-xl font-semibold text-sm transition-all duration-200 flex items-center justify-center gap-2"
              style={{ background: 'rgba(124,58,237,0.15)', border: '1px solid rgba(124,58,237,0.3)', color: '#C4B5FD' }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(124,58,237,0.25)'; (e.currentTarget as HTMLElement).style.borderColor = 'rgba(124,58,237,0.6)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(124,58,237,0.15)'; (e.currentTarget as HTMLElement).style.borderColor = 'rgba(124,58,237,0.3)'; }}
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                <path d="M11.5 1a.5.5 0 0 1 0 1H11a.5.5 0 0 0-.5.5v11a.5.5 0 0 0 .5.5h.5a.5.5 0 0 1 0 1h-7a.5.5 0 0 1 0-1H5a.5.5 0 0 0 .5-.5v-11A.5.5 0 0 0 5 2h-.5a.5.5 0 0 1 0-1h7z"/>
              </svg>
              Read full article in Reader app
            </button>
          </div>
        </div>

        {/* Side stack */}
        <div className="lg:col-span-2 flex flex-col gap-4">
          {sideArticles.map((art, i) => (
            <div key={i} className="article-card rounded-2xl p-5 cursor-pointer group fade-in-up">
              <div className="flex items-start gap-2 mb-3">
                <span className="tag-pill">{art.category}</span>
                <span className={`tag-pill ${art.diffClass} ml-auto`}>{art.difficulty}</span>
              </div>
              <h4 className="text-sm font-semibold leading-snug mb-2" style={{ color: '#F4F4F5' }}>{art.title}</h4>
              <div className="card-summary">
                <p className="text-xs leading-relaxed" style={{ color: '#71717A' }}>{art.summary}</p>
              </div>
              <div className="flex items-center justify-between mt-3 pt-3" style={{ borderTop: '1px solid rgba(124,58,237,0.08)' }}>
                <span className="font-mono text-xs" style={{ color: '#52525B' }}>{art.readTime} · ⬡ {art.forked}</span>
                <span className="read-badge text-xs font-semibold" style={{ color: '#7C3AED' }}>Read in App →</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const sideArticles = [
  {
    title: 'Polymorphic linked records: the schema pattern agencies won\'t share',
    summary: 'One junction table, multiple parent types, infinite flexibility for repeatable client deliverables.',
    category: 'Schema Design', difficulty: 'Advanced', diffClass: 'difficulty-advanced',
    readTime: '11 min', forked: 412,
  },
  {
    title: 'LLM-generated scripts: the exact Airtable context you must provide',
    summary: 'GPT-4 writes decent scripts when you paste your schema. Here\'s the exact prompt template.',
    category: 'Scripting', difficulty: 'Intermediate', diffClass: 'difficulty-intermediate',
    readTime: '10 min', forked: 738,
  },
  {
    title: 'Airtable REST API rate limits: a practical survival guide',
    summary: 'Five requests per second sounds fine until your Make scenario hits 400 records at 2 a.m.',
    category: 'Integrations', difficulty: 'Intermediate', diffClass: 'difficulty-intermediate',
    readTime: '7 min', forked: 329,
  },
];